/**
 * The prerendered, auth-free marketing surface. Keep in sync with the
 * prerender `pages` list in `vite.config.ts`.
 *
 * Routes matched here render WITHOUT the app shell providers (see
 * `RootDocument` in `src/routes/__root.tsx`): no Lingui/`LocaleProvider`,
 * no Sentry, no upload store, no toast or tooltip runtime. A component
 * rendered on these routes must not call `useLingui`, `<Trans>`, `toast`,
 * or `<Tooltip>` — marketing copy is hardcoded pt-BR by design.
 */
const MARKETING_EXACT_PATHS = [
  '/',
  '/jornada',
  '/termos-de-uso',
  '/politica-de-privacidade',
] as const

const MARKETING_PREFIXES = ['/recursos/', '/pt/', '/en/'] as const

const MARKETING_EXACT: ReadonlySet<string> = new Set(MARKETING_EXACT_PATHS)

/**
 * Any path on the marketing surface. Derived from the same two lists the
 * runtime check uses, so a new marketing route widens both at once. Consumed
 * by `<MarketingLink/>`, which is the only supported way to link here from an
 * app route.
 */
export type MarketingPath =
  | (typeof MARKETING_EXACT_PATHS)[number]
  | `${(typeof MARKETING_PREFIXES)[number]}${string}`

export const isMarketingPath = (pathname: string): boolean => {
  const normalized =
    pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
  if (MARKETING_EXACT.has(normalized)) {
    return true
  }
  // `${normalized}/` makes the prefix test also match the bare prefix path
  // itself (`/pt`, `/en`, `/recursos`).
  return MARKETING_PREFIXES.some((prefix) => `${normalized}/`.startsWith(prefix))
}
